import * as React from 'react';
import {
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { KittingRule, PRIORITY_LABELS } from '../types';

function componentsSummary(rule: KittingRule): string {
  return rule.components.map(c => `${c.quantity} × ${c.sku}`).join('  +  ');
}

// The substitution rules, with inline enable/disable. All edits save the full
// rules set (the parent owns persistence).
export default function RulesTable({
  rules,
  saving,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  onPreview,
}: {
  rules: KittingRule[];
  saving: boolean;
  onToggle: (rule: KittingRule, enabled: boolean) => void;
  onAdd: () => void;
  onEdit: (rule: KittingRule) => void;
  onDelete: (rule: KittingRule) => void;
  onPreview: (rule: KittingRule) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Rules</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
          Add rule
        </Button>
      </Stack>
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No rules yet — add one to start kitting.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Enabled</TableCell>
                <TableCell>
                  <Tooltip title="When two rules want the same items, the higher priority (lower number) takes them first">
                    <span>Priority</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Components</TableCell>
                <TableCell>Kit</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(rule => (
                <TableRow key={rule.ruleId} sx={rule.enabled ? {} : { opacity: 0.55 }}>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={rule.enabled}
                      disabled={saving}
                      onChange={e => onToggle(rule, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={PRIORITY_LABELS[rule.priority] ?? String(rule.priority)}>
                      <Chip label={rule.priority} size="small" variant="outlined" />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">{rule.ruleId}</Typography>
                      {rule.description && (
                        <Typography variant="caption" color="text.secondary">
                          {rule.description}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {componentsSummary(rule)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={rule.store} size="small" />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {rule.kitSku || rule.kitVariantId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview against real orders">
                      <IconButton size="small" onClick={() => onPreview(rule)}>
                        <ScienceOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" disabled={saving} onClick={() => onEdit(rule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" disabled={saving} onClick={() => onDelete(rule)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
